(() => {
  'use strict';
  const PREFIX = 'maya_', AXES = ['width', 'length'];
  const caches = new Map();
  let controller, generation = 0, controls;
  const params = () => new URL(location.href).searchParams;
  const active = () => AXES.some(a => ['min', 'max'].some(b => params().has(`${PREFIX}${a}_${b}`)));
  const cancel = () => { generation++; controller?.abort(); };
  const feet = inches => `${Math.floor(inches / 12)}' ${Math.round(inches % 12)}"`;
  function titleDimensions(title) {
    const m = String(title || '').match(/(\d+)\s*['′]\s*(?:(\d+)\s*["″])?\s*[xX×]\s*(\d+)\s*['′]\s*(?:(\d+)\s*["″])?/);
    return m ? [12 * +m[1] + +(m[2] || 0), 12 * +m[3] + +(m[4] || 0)] : [];
  }
  function measurement(raw, fallback) {
    if (typeof raw === 'number') return raw > 0 ? raw / 2.54 : fallback;
    const s = String(raw || '').trim();
    const n = s.match(/(?:^|[^\d])([\d]+(?:[.,]\d+)?)/);
    if (!n) return fallback;
    const cm = Number(n[1].replace(',', '.'));
    if (!(cm > 0)) return fallback;
    // Explicit cm is authoritative. For legacy unitless text, confirm the inferred unit against the title.
    if (/cm|centim[èe]tr/i.test(s) || (fallback && Math.abs(cm - fallback * 2.54) <= 3)) return cm / 2.54;
    return fallback;
  }
  function dimensions(product) {
    const t = titleDimensions(product.title);
    const inch=value=>Number.isFinite(value)?Math.round(value):undefined;
    return { ...product, width: inch(measurement(product.width, t[0])), length: inch(measurement(product.length, t[1])) };
  }
  function normalizedURL(input, section, page = 1, unfiltered = false) {
    const u = new URL(input, location.origin);
    for (const k of [...u.searchParams.keys()]) {
      if (k.startsWith(PREFIX) || k === 'page' || k === 'sections' || k === 'section_id' || (unfiltered && k.startsWith('filter.'))) u.searchParams.delete(k);
    }
    u.searchParams.set('section_id', section);
    u.searchParams.set('page', page);
    return u;
  }
  async function section(input, name, page, signal, unfiltered) {
    const u = normalizedURL(input, name, page, unfiltered), k = u.href;
    if (!caches.has(k)) {
      const job = fetch(u, { signal, credentials: 'same-origin' }).then(async response => {
        if (!response.ok) throw new Error(`Section ${response.status}`);
        const html = await response.text();
        return new DOMParser().parseFromString(html, 'text/html');
      }).catch(error => { caches.delete(k); throw error; });
      caches.set(k, job);
    }
    return caches.get(k);
  }
  async function index(input, signal, unfiltered = false) {
    async function page(n) {
      const doc = await section(input, 'maya-dimension-index', n, signal, unfiltered);
      const block = doc.querySelector('[data-maya-dimension-index]');
      if (!block) throw new Error('Missing dimension index');
      const result = JSON.parse(block.textContent);
      if (!Array.isArray(result.products) || !Number.isInteger(result.pages) || result.pages > 100) throw new Error('Invalid dimension index');
      return result;
    }
    const first = await page(1), all = [...first.products];
    // All pages are required before returning a result, including a collection larger than Shopify's per-page limit.
    for (let n = 2; n <= first.pages; n++) all.push(...(await page(n)).products);
    if (all.length !== first.total) throw new Error('Incomplete dimension index');
    return all.map(dimensions);
  }
  const text = (k, replacements = {}) => Object.entries(replacements).reduce((s, [a, b]) => s.replace(`{${a}}`, b), controls?.labels[k] || k);
  function boundsFromURL() {
    const p = params(), result = {};
    for (const a of AXES) for (const b of ['min','max']) {
      const k = `${PREFIX}${a}_${b}`, v = p.get(k);
      if (v !== null && v !== '' && Number.isFinite(+v) && +v >= 0) result[`${a}_${b}`] = +v;
    }
    return result;
  }
  function matches(p, selected) {
    return AXES.every(a => {
      const min = selected[`${a}_min`], max = selected[`${a}_max`];
      if (min === undefined && max === undefined) return true;
      return Number.isFinite(p[a]) && (min === undefined || p[a] >= min - 0.001) && (max === undefined || p[a] <= max + 0.001);
    });
  }
  function summary(nav, message, error = false) {
    let node = nav.body.querySelector('[data-maya-dimension-summary]');
    if (!node) {
      node = document.createElement('div');node.dataset.mayaDimensionSummary = '';node.className = 'maya-dimensions__summary';
      nav.body.insertBefore(node, nav.grid_container);
    }
    node.replaceChildren();node.setAttribute('role', error ? 'alert' : 'status');
    const label = document.createElement('span');label.textContent = message;node.append(label);
    if (error) {
      const retry = document.createElement('button');retry.type = 'button';retry.textContent = text('retry');retry.onclick = () => render(nav);node.append(retry);
    }
    const clear = document.createElement('button');clear.type = 'button';clear.textContent = text('reset');clear.onclick = () => controls.clear(true);node.append(clear);
  }
  async function render(nav) {
    cancel(); const token = generation; controller = new AbortController();const signal = controller.signal;
    nav.abort_controller?.abort();
    nav.showLoadingView();
    summary(nav, text('loading'));
    const request = new URL(nav.ajax_url || location.href, location.origin);
    try {
      const products = await index(request, signal);
      const selected = boundsFromURL();
      const matched = products.map((p, i) => ({ p, i })).filter(({p}) => matches(p, selected));
      const pages = Math.max(1, Math.ceil(matched.length / 48));
      const current = Math.min(pages, Math.max(1, Math.floor(+params().get('maya_dimension_page') || 1)));
      const chosen = matched.slice((current-1)*48, current*48);
      const pageNumbers = [...new Set(chosen.map(({i}) => Math.floor(i/50)+1))];
      const templates = new Map();
      // Small bounded batches avoid flooding the storefront when a match spans many source pages.
      for (let start=0; start<pageNumbers.length; start+=3) {
        await Promise.all(pageNumbers.slice(start,start+3).map(async n => {
          const doc = await section(request, 'maya-dimension-cards', n, signal);
          if (!doc.querySelector('[data-maya-dimension-cards]')) throw new Error('Missing product cards');
          doc.querySelectorAll('template[data-dimension-product]').forEach(t => templates.set(t.dataset.dimensionProduct, t));
        }));
      }
      if (signal.aborted || token !== generation) return;
      const fragment = document.createDocumentFragment();
      for (const {p} of chosen) {
        const card = templates.get(String(p.id));
        if (!card || !card.content.children.length) throw new Error('Incomplete product cards');
        fragment.append(card.content.cloneNode(true));
      }
      nav.grid_container.replaceChildren(fragment);
      if (!matched.length) { const empty=document.createElement('p');empty.className='maya-dimensions__empty';empty.textContent=text('empty');nav.grid_container.append(empty); }
      nav.grid_container.removeAttribute('style');nav.spinner.style.display='none';
      nav.pagination.replaceChildren();
      if (pages > 1) {
        const row=document.createElement('nav');row.className='maya-dimensions__pagination';row.setAttribute('aria-label',text('page',{page:current,pages}));
        for (const [label,number,disabled] of [[text('previous'),current-1,current===1],[text('next'),current+1,current===pages]]) {
          const button=document.createElement('button');button.type='button';button.textContent=label;button.disabled=disabled;
          button.onclick=()=>{const u=new URL(location.href);u.searchParams.set('maya_dimension_page',number);history.pushState({},'',u);nav.ajax_url=u.href;render(nav).then(()=>nav.body.scrollIntoView({block:'start',behavior:'smooth'}));};
          row.append(button);
          if(number===current-1){const span=document.createElement('span');span.textContent=text('page',{page:current,pages});row.append(span);}
        }
        nav.pagination.append(row);nav.pagination.removeAttribute('style');
      } else nav.pagination.style.display='none';
      if(nav.pagination_link)nav.pagination_link.style.display='none';
      summary(nav,text('count',{count:matched.length}));
      window.theme?.quick_add?.clearForms?.();nav.formatProducts();
    } catch(error) {
      if(signal.aborted || token !== generation) return;
      caches.clear();
      nav.grid_container.replaceChildren();nav.grid_container.removeAttribute('style');nav.spinner.style.display='none';nav.pagination.style.display='none';
      summary(nav,text('error'),true);
    } finally { if(token===generation)document.documentElement.classList.remove('maya-dimension-pending'); }
  }
  async function navigation() {
    await customElements.whenDefined('navigation-root');
    const nav=document.querySelector('navigation-root');
    for(let n=0;nav && !nav.grid_container && n<100;n++)await new Promise(resolve=>setTimeout(resolve,20));
    if(!nav?.grid_container)throw new Error('Navigation unavailable');
    return nav;
  }
  class Dimensions extends HTMLElement {
    connectedCallback() {
      // Native theme moves this element into its filter drawer. Preserve listeners and state during that move.
      controls=this;if(this.initialized)return;this.initialized=true;
      this.labels=JSON.parse(this.querySelector('[data-dimension-text]').textContent);this.status=this.querySelector('[data-dimension-status]');
      this.addEventListener('input',e=>{if(e.target.matches('input[type=range]'))this.sync(e.target);});
      this.addEventListener('change',e=>{if(e.target.matches('input[type=range]'))this.apply();});
      this.querySelector('[data-clear-dimensions]').onclick=()=>this.clear(true);
      const reset=e=>{if(e.type==='keydown'&&e.key!=='Enter')return;if(e.target.closest('.navigation--active-clear,.filter--button[data-type=reset]')){this.clear(false);setTimeout(()=>this.apply(),0);}};
      document.addEventListener('click',reset,true);document.addEventListener('keydown',reset,true);
      window.addEventListener('popstate',async()=>{this.restore();const nav=await navigation();nav.ajax_url=location.href;nav.showLoadingView();nav.getGridHtml();});
      this.initialize();
    }
    async initialize() {
      try {
        const products=await index(location.href,undefined,true);
        for(const a of AXES){
          const values=products.map(p=>p[a]).filter(v=>Number.isFinite(v)&&v>0);
          const field=this.querySelector(`[data-axis=${a}]`);
          if(!values.length)continue;
          const min=Math.floor(Math.min(...values)),max=Math.ceil(Math.max(...values));
          field.disabled=false;
          field.querySelectorAll('input[type=range]').forEach(i=>{i.min=min;i.max=max;i.step=1;});
          field.dataset.min=min;field.dataset.max=max;
        }
        this.restore();this.status.textContent='';
        if(active()){const nav=await navigation();nav.ajax_url=location.href;render(nav);}
      } catch(error) {
        caches.clear();
        this.status.textContent=text('error');
        const retry=document.createElement('button');retry.type='button';retry.textContent=text('retry');retry.onclick=()=>{retry.remove();this.initialize();};this.status.append(' ',retry);
        if(active()){const nav=await navigation();nav.showLoadingView();nav.spinner.style.display='none';summary(nav,text('error'),true);}
        document.documentElement.classList.remove('maya-dimension-pending');
      }
    }
    restore() {
      const p=boundsFromURL();
      for(const a of AXES){const f=this.querySelector(`[data-axis=${a}]`);if(f.disabled)continue;
        f.querySelector('[data-bound=min]').value=p[`${a}_min`]??f.dataset.min;
        f.querySelector('[data-bound=max]').value=p[`${a}_max`]??f.dataset.max;
      }
      this.sync();
    }
    sync(changed) {
      let any=false;
      for(const a of AXES){
        const f=this.querySelector(`[data-axis=${a}]`);if(f.disabled)continue;
        const lo=f.querySelector('[data-bound=min]'),hi=f.querySelector('[data-bound=max]');
        if(+lo.value>+hi.value){if(changed===lo)hi.value=lo.value;else lo.value=hi.value;}
        const isSelected=+lo.value>+f.dataset.min || +hi.value<+f.dataset.max;
        for(const [bound,input] of [['min',lo],['max',hi]]){
          input.setAttribute('aria-valuetext',feet(+input.value));
          const hidden=f.querySelector(`[data-param=${bound}]`);hidden.value=input.value;hidden.disabled=!isSelected;
        }
        f.querySelector('output').textContent=feet(+lo.value)+' – '+feet(+hi.value);
        const range=f.querySelector('.maya-dimensions__range'),span=+hi.max-+lo.min || 1;
        range.style.setProperty('--range-start',100*(+lo.value-+lo.min)/span+'%');range.style.setProperty('--range-end',100*(+hi.value-+lo.min)/span+'%');
        any ||= isSelected;
      }
      this.querySelector('[data-clear-dimensions]').hidden=!any;
    }
    async apply(){
      this.sync();const u=new URL(location.href);for(const a of AXES)for(const b of ['min','max'])u.searchParams.delete(`${PREFIX}${a}_${b}`);
      this.querySelectorAll('input[type=hidden]:not(:disabled)').forEach(i=>u.searchParams.set(i.name,i.value));
      u.searchParams.delete('page');u.searchParams.delete('maya_dimension_page');history.pushState({},'',u);
      const nav=await navigation();nav.ajax_url=u.href;nav.showLoadingView();
      if(!active())nav.body.querySelector('[data-maya-dimension-summary]')?.remove();
      nav.getGridHtml();
    }
    clear(apply){for(const a of AXES){const f=this.querySelector(`[data-axis=${a}]`);if(f.disabled)continue;f.querySelector('[data-bound=min]').value=f.dataset.min;f.querySelector('[data-bound=max]').value=f.dataset.max;}this.sync();if(apply)this.apply();}
  }
  window.GalerieMayaDimensions={isActive:active,cancel,render,dimensions,matches,titleDimensions};
  if(!customElements.get('maya-dimensions'))customElements.define('maya-dimensions',Dimensions);
})();
